"use client";
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputBase, Link, Paper, Radio, RadioGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import SubmitButtonComponent from "@/components/buttons/SubmitButtonComponent";
import OtpModal from "./OtpModal";
import NumberFoundModal from "./NumberFoundModal";
import LoaderComponent from "@/components/loader/LoaderComponent";
import { useGenerateOtpMutation, useLazyGetContactHintQuery, useVerifyotpMutation } from "@/lib/store";
import { enqueueSnackbar } from "notistack";
import { MOBILE_REGEX, NUMBER_REGEX, errorMessageText } from "@/lib/Constants";
import { phoneNumberSchema } from "@/lib/validationSchema/phoneNumberSchema";
import { setupMoengageUser } from "@/lib/moengage/moengageHelpers.js";
import { setServiceProvider } from "@/lib/store/slices/serviceProviderSlice";
import styles from "./styles.module.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Slider = dynamic(() => import("react-slick"), { ssr: false });

const imageSliderSettings = {
  infinite: true,
  speed: 500,
  slidesToShow: 2.5,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2000,
  arrows: false,
  pauseOnHover: false,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "20%",
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "15%",
      },
    },
  ],
};

const LoginScreen = () => {

  //HOOKS
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useDispatch()
  const moengageRef = useRef(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [phoneNumber, setPhoneNumber] = useState("");
  const [appId, setAppId] = useState("");
  const [appIdNew, setAppIdNew] = useState("");
  const [check, setCheck] = useState(true);
  const [otp, setOtp] = useState("");
  const [openNumberFoundModal, setOpenNumberFoundModal] = useState(false);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [resendOtp, setResendOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLoginOption, setSelectedLoginOption] = useState("appId");
  const [isOfferNotEligible, setIsOfferNotEligible] = useState(false);
  const [offerNotEligibleMsg, setOfferNotEligibleMsg] = useState("");
  const [checkboxState, setCheckboxState] = useState({
    agreeToTerms: true,
    whatsappUpdates: true,
  });

  const [
    getContactHint,
    {
      data: contactHintData,
      isFetching: contactHintFetching,
      isLoading: contactHintLoading,
      error: contactHintError,
      isError: isContactHintError,
      isSuccess: contactHintIsSuccess,
    },] = useLazyGetContactHintQuery();

  const [
    generateOtp,
    {
      data: generateOtpData,
      isLoading: generateOtpLoading,
      isError: generateOtpIsError,
      error: generateOtpError,
    },
  ] = useGenerateOtpMutation();

  const [
    validateOtp,
    {
      data: verifyOtpData,
      isLoading: verifyOtpLoading,
      error: verifyOtpError,
      isError: isVerifyOtpError,
    },
  ] = useVerifyotpMutation();

  const initialValue = {
    phoneNumber: "",
  };

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: phoneNumberSchema,
    onSubmit: (values) => {
    },
  });

  useEffect(() => {
    if (contactHintError !== null && contactHintError !== undefined && isContactHintError) {
      setIsOfferNotEligible(true);
      setOfferNotEligibleMsg(contactHintError?.data?.message || errorMessageText);
      enqueueSnackbar(contactHintError?.data?.message || errorMessageText, {
        variant: "error",
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    }
  }, [contactHintError]);

  useEffect(() => {
    if (generateOtpError !== null && generateOtpError !== undefined && generateOtpIsError) {
      setIsOfferNotEligible(true);
      setOfferNotEligibleMsg(generateOtpError?.data?.message || errorMessageText);
      if (generateOtpError?.status === 404) {
        enqueueSnackbar(generateOtpError?.data?.message || errorMessageText, {
          variant: "error",
          autoHideDuration: 3000,
          preventDuplicate: true,
        });
        setAppId("");
      } else {
        enqueueSnackbar(generateOtpError?.data?.message || errorMessageText, {
          variant: "error",
          autoHideDuration: 3000,
          preventDuplicate: true,
        });
      }
    }
  }, [generateOtpError]);

  useEffect(() => {
    if (verifyOtpError !== null && verifyOtpError !== undefined && isVerifyOtpError) {
      setIsOfferNotEligible(true);
      setOfferNotEligibleMsg(verifyOtpError?.data?.message || errorMessageText);
      enqueueSnackbar(verifyOtpError?.data?.message || errorMessageText, {
        variant: "error",
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    }
  }, [verifyOtpError]);

  useEffect(() => {
    if (contactHintLoading || generateOtpLoading || verifyOtpLoading || contactHintFetching) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [contactHintLoading, generateOtpLoading, verifyOtpLoading, contactHintFetching,]);

  useEffect(() => {
    if (appId !== "" && check) {
      setCheck(false);
    } else if (appId === "" && !check) {
      setCheck(true);
    }
  }, [appId, check]);

  useEffect(() => {
    if (
      contactHintData?.status === 200 &&
      contactHintData?.data?.phone_number &&
      !check &&
      !isContactHintError &&
      !contactHintFetching &&
      contactHintIsSuccess
    ) {
      setPhoneNumber(contactHintData?.data?.phone_number);
      setOpenNumberFoundModal(true);
    }
  }, [
    contactHintData,
    check,
    isContactHintError,
    contactHintFetching,
    contactHintIsSuccess,
  ]);

  useEffect(() => {
    if (generateOtpData !== null && generateOtpData !== undefined) {
      if (!openOtpModal && !resendOtp) {
        setOpenNumberFoundModal(false);
        setOpenOtpModal(true);
      } else {
        setResendOtp(true);
      }
    }
  }, [generateOtpData]);

  useEffect(() => {
    const handleMoengageUser = async () => {
      if (!verifyOtpData || moengageRef.current) return;
      moengageRef.current = true;

      const moengageUserId = appId ? generateOtpData?.phone_number : phoneNumber;

      await setupMoengageUser({ verifyOtpData, moengageUserId });

      sessionStorage.token = verifyOtpData?.access_token;
      sessionStorage.appId = verifyOtpData?.loan_id;
      sessionStorage.username = `${verifyOtpData?.user?.firstname || ""} ${verifyOtpData?.user?.lastname || ""}`;

      dispatch(setServiceProvider(verifyOtpData?.service_provider));
      router.push("/lpl");
    };

    handleMoengageUser();
  }, [verifyOtpData]);

  //FUNCTIONS

  const handleNumberFoundModalClose = (bool) => {
    setOpenNumberFoundModal(bool);

    setAppId("");
    setAppIdNew("");
    setPhoneNumber("");
    formik.resetForm();

    setCheck(true);
    setIsOfferNotEligible(false);
  };

  const handleOtpModalClose = (bool) => {
    setOpenOtpModal(bool);

    setAppId("");
    setAppIdNew("");
    setPhoneNumber("");
    formik.resetForm();

    setOtp("");
    setCheck(true);
    setIsOfferNotEligible(false);
  };

  const handleVerifyPhoneNumber = (phoneNumber) => {
    setIsOfferNotEligible(false);
    generateOtp({ phone_number: phoneNumber });
    setPhoneNumber(phoneNumber);
  };

  const handleVerifyAppId = (appIdValue) => {
    setIsOfferNotEligible(false);
    setAppId(appIdValue);
    getContactHint(appIdValue);
  };

  const handleHitSendOtp = () => {
    if (appId !== "") {
      generateOtp({ phone_number: phoneNumber, app_id: appId });
    } else {
      generateOtp({ phone_number: phoneNumber });
    }
  };

  const handleVerifyOtp = (bool, otp) => {
    validateOtp({
      phone_number: phoneNumber,
      app_id: appId,
      otp: otp,
    });
  };

  const handleMobileNumberChange = (event) => {
    setIsOfferNotEligible(false);
    const isValid = MOBILE_REGEX(event.target.value);
    if (isValid) {
      formik.handleChange(event);
      setPhoneNumber(event.target.value);
    }
  }

  const handleAppIdChange = (event) => {
    setIsOfferNotEligible(false);

    const value = event.target.value;

    const isValid = NUMBER_REGEX(value);
    if (!isValid) return;

    if (value.length > 10) return;

    setAppIdNew(value);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckboxState((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  return (
    <>
      <LoaderComponent open={loading} />
      <Paper className={styles["login-paper"]}>
        <Box className={styles["login-box"]}>
          {/* MOBILE VIEW */}
          {isMobile ? (
            <Grid container className={styles["login-grid-container"]}>
              <Grid item xs={12} className={styles["login-left-grid"]}>
                <Box className={styles["hero-logo-img-box"]}>
                  <Image src="/logo.svg" alt="Hero FinCorp Logo" width={120} height={120} priority />
                </Box>

                <Box className={styles["left-center-box"]} gap={2}>
                  <Button variant="outlined" className={styles["left-center-button"]}>Your Loan. Your Choice. Your Way.</Button>
                  <Typography variant="h4" className={styles["left-heading"]}>
                    Turn your plans into reality with Hero FinCorp!
                  </Typography>
                  <Typography variant="h4" className={styles["left-subheading"]}>
                    Upto <strong>₹ 5 Lakh</strong> Pre-Approved*, Just a Click Away
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} className={styles["login-form-grid"]}>
                <Box className={styles["login-form-box"]}>
                  <Typography className={styles["login-text"]} mb={2}>
                    {selectedLoginOption === "mobileNumber"
                      ? "What's Your Phone Number?"
                      : selectedLoginOption === "appId"
                        ? "What's Your Application ID?"
                        : "Login with"}
                  </Typography>

                  <Box className={styles["login-textfield-box"]}>
                    <Box sx={{ width: 10 }}>
                      <Image src="/login_left.svg" alt="left" width={20} height={20} style={{ width: "100%", height: "auto" }} priority />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      {selectedLoginOption === "appId" ? (() => {
                        const isDisabled = !appIdNew || !checkboxState.agreeToTerms;

                        return (
                          <Box className={styles["mobile-text-input-container"]}>
                            <InputBase
                              name="appId"
                              placeholder="Enter Application Id"
                              maxLength={10}
                              value={appIdNew}
                              className={styles["mobile-text-input-base"]}
                              onChange={handleAppIdChange}
                            />
                            <SubmitButtonComponent
                              variant="text"
                              type="submit"
                              className={`${styles["mobile-otp-button"]} ${isDisabled ? styles["mobile-otp-button-disabled"] : styles["mobile-otp-button-enabled"]}`}
                              disabled={isDisabled}
                              onClick={() => handleVerifyAppId(appIdNew)}
                            >
                              Get OTP
                            </SubmitButtonComponent>
                          </Box>
                        );
                      })() : (() => {
                        const isDisabled = !(formik.isValid && formik.dirty && checkboxState.agreeToTerms);

                        return (
                          <>
                            <Box className={styles["mobile-text-input-container"]}>
                              <InputBase
                                name="phoneNumber"
                                placeholder="Enter your Registered Phone Number"
                                maxLength={10}
                                value={formik.values.phoneNumber}
                                className={`${styles["mobile-text-input-base"]} ${formik.touched.phoneNumber && formik.errors.phoneNumber
                                  ? styles["mobile-text-input-error"]
                                  : ""
                                  }`}
                                onChange={(event) => {
                                  formik.setFieldTouched("phoneNumber", true);
                                  handleMobileNumberChange(event);
                                }}
                                onBlur={formik.handleBlur}
                              />
                              <SubmitButtonComponent
                                variant="text"
                                type="submit"
                                className={`${styles["mobile-otp-button"]} ${isDisabled ? styles["mobile-otp-button-disabled"] : styles["mobile-otp-button-enabled"]}`}
                                disabled={isDisabled}
                                onClick={() => handleVerifyPhoneNumber(formik.values.phoneNumber)}
                              >
                                Get OTP
                              </SubmitButtonComponent>
                            </Box>
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <Box className={styles["mobile-input-error-text"]}>
                                {formik.errors.phoneNumber}
                              </Box>
                            )}
                          </>
                        );
                      })()}

                    </Box>
                    <Box sx={{ width: 10 }}>
                      <Image src="/login_right.svg" alt="right" width={20} height={20} style={{ width: "100%", height: "auto" }} priority />
                    </Box>
                  </Box>

                  <FormControl fullWidth>
                    <RadioGroup
                      row
                      className={styles["mobile-radio-group-horizontal"]}
                      value={selectedLoginOption}
                      onChange={(e) => {
                        setSelectedLoginOption(e.target.value);
                        formik.resetForm();
                        setAppIdNew("");
                        setPhoneNumber("");
                        setIsOfferNotEligible(false);
                      }}
                    >
                      <FormControlLabel
                        value="appId"
                        control={<Radio className={styles["login-select-btn"]} />}
                        label="Application ID"
                        className={styles["mobile-radio-label"]}
                      />
                      <FormControlLabel
                        value="mobileNumber"
                        control={<Radio className={styles["login-select-btn"]} />}
                        label="Phone Number"
                        className={styles["mobile-radio-label"]}
                      />
                    </RadioGroup>
                  </FormControl>

                  {isOfferNotEligible && (
                    <Alert variant="filled" severity="error" sx={{ mb: 1 }}>
                      {offerNotEligibleMsg}
                    </Alert>
                  )}

                  <FormControl fullWidth className={styles["mobile-checkbox-group"]}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="agreeToTerms"
                          className={styles["login-select-btn"]}
                          checked={checkboxState.agreeToTerms}
                          onChange={handleCheckboxChange}
                        />}
                      label={
                        <span>
                          By proceeding, you agree to our{" "}
                          <Link
                            href="https://loans.apps.herofincorp.com/en/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["terms-conditions-link-text"]}
                          >
                            Terms and Conditions
                          </Link>{" "}
                          and{" "}
                          <Link
                            href="https://www.herofincorp.com/data-privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["terms-conditions-link-text"]}
                          >
                            Privacy Policy
                          </Link>
                          .
                        </span>
                      }
                      className={styles["mobile-checkbox-label"]}
                    />
                    {/* <FormControlLabel
                      control={
                        <Checkbox
                          name="whatsappUpdates"
                          className={styles["login-select-btn"]}
                          checked={checkboxState.whatsappUpdates}
                          onChange={handleCheckboxChange}
                        />}
                      label={
                        <Box className={styles["whatsapp-icon-box"]}>
                          Stay updated on all the alerts on&nbsp;
                          <Image src="/whatsapp_icon.svg" alt="whatsapp icon" width={15} height={15} priority />
                          &nbsp;WhatsApp
                        </Box>
                      }
                      className={styles["mobile-checkbox-label"]}
                    /> */}
                  </FormControl>
                </Box>
              </Grid>

              <Box className={styles["login-image-slider-box"]}>
                <Slider {...imageSliderSettings}>
                  {["img1.jpg", "img2.jpg", "img3.jpg", "img4.jpg", "img5.jpg", "img6.jpg", "img7.jpg"].map((img, index) => (
                    <div key={index} className={styles["slider-item"]}>
                      <Image
                        src={`/login_slider_${img}`}
                        alt={`Slide ${index}`}
                        width={300}
                        height={200}
                        className={styles["slider-image"]}
                        priority
                      />
                    </div>
                  ))}
                </Slider>
              </Box>
            </Grid>
          ) : (
            // DESKTOP VIEW
            <>
              <Box className={styles["hero-logo-box"]}>
                <Image src="/logo.svg" alt="Logo" width={180} height={180} priority />
              </Box>

              {/* Main Grid */}
              <Grid container spacing={0} alignItems="stretch" className={styles["login-grid-container"]}>
                {/* Left Grid */}
                <Grid item xs={12} md={6} className={styles["left-grid-container"]}>
                  <Box gap={4} className={styles["left-grid-box"]}>
                    <Button variant="outlined" className={styles["left-center-button"]}>Your Loan. Your Choice. Your Way.</Button>
                    <Typography variant="h4" className={styles["left-heading"]}>
                      Turn your plans into reality with Hero FinCorp!
                    </Typography>
                    <Typography variant="h4" className={styles["left-subheading"]}>
                      Upto <strong>₹ 5 Lakh</strong> Pre-Approved*, Just a Click Away
                    </Typography>

                    {/* Image Slider */}
                    <Box className={styles["slider-box"]}>
                      <Slider {...imageSliderSettings}>
                        {["img1.jpg", "img2.jpg", "img3.jpg", "img4.jpg", "img5.jpg", "img6.jpg", "img7.jpg"].map((img, index) => (
                          <div key={index} className={styles["slider-item"]}>
                            <Image
                              src={`/login_slider_${img}`}
                              alt={`Slide ${index}`}
                              width={300}
                              height={200}
                              className={styles["slider-image"]}
                              priority
                            />
                          </div>
                        ))}
                      </Slider>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Grid */}
                <Grid item xs={12} md={6} className={styles["login-form-grid"]}>
                  <Box className={styles["login-form-box"]}>
                    <Typography className={styles["login-text"]} mb={2}>
                      {selectedLoginOption === "mobileNumber"
                        ? "What's Your Phone Number?"
                        : selectedLoginOption === "appId"
                          ? "What's Your Application ID?"
                          : "Login with"}
                    </Typography>

                    <Box className={styles["login-textfield-box"]}>
                      <Box sx={{ width: 16 }}>
                        <Image src="/login_left.svg" alt="left" width={20} height={20} style={{ width: "100%", height: "auto" }} priority />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        {selectedLoginOption === "appId" ? (
                          <Box className={styles["input-base-container"]}>
                            <InputBase
                              name="appId"
                              placeholder="Enter Application Id"
                              maxLength={10}
                              value={appIdNew}
                              className={styles["input-base"]}
                              onChange={handleAppIdChange}
                            />
                          </Box>
                        ) : (
                          <>
                            <Box className={styles["input-base-container"]}>
                              <InputBase
                                name="phoneNumber"
                                placeholder="Enter your Registered Phone Number"
                                maxLength={10}
                                value={formik.values.phoneNumber}
                                className={styles["input-base"]}
                                onChange={(event) => {
                                  formik.setFieldTouched("phoneNumber", true);
                                  handleMobileNumberChange(event);
                                }}
                                onBlur={formik.handleBlur}
                              />
                            </Box>
                            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                              <Box className={styles["mobile-input-error-text"]}>
                                {formik.errors.phoneNumber}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                      <Box sx={{ width: 16 }}>
                        <Image src="/login_right.svg" alt="right" width={20} height={20} style={{ width: "100%", height: "auto" }} priority />
                      </Box>
                    </Box>

                    <FormControl fullWidth sx={{ mb: isOfferNotEligible ? 1 : 4, pl: 5, pr: 5, mt: 2 }}>
                      <RadioGroup
                        row
                        value={selectedLoginOption}
                        onChange={(e) => {
                          setSelectedLoginOption(e.target.value);
                          formik.resetForm();
                          setAppIdNew("");
                          setPhoneNumber("");
                          setIsOfferNotEligible(false);
                        }}
                      >
                        <FormControlLabel
                          value="appId"
                          control={<Radio className={styles["login-select-btn"]} />}
                          label="Application ID"
                          className={styles["white-text-color"]}
                        />
                        <FormControlLabel
                          value="mobileNumber"
                          control={<Radio className={styles["login-select-btn"]} />}
                          label="Phone Number"
                          className={styles["white-text-color"]}
                        />
                      </RadioGroup>
                    </FormControl>

                    {isOfferNotEligible && (
                      <Alert variant="filled" severity="error" sx={{ mb: 1 }}>
                        {offerNotEligibleMsg}
                      </Alert>
                    )}

                    <FormControl fullWidth sx={{ mb: 4, pl: 5, pr: 5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="agreeToTerms"
                            className={styles["login-select-btn"]}
                            checked={checkboxState.agreeToTerms}
                            onChange={handleCheckboxChange}
                          />}
                        label={
                          <span>
                            By proceeding, you agree to our{" "}
                            <Link
                              href="https://loans.apps.herofincorp.com/en/terms-and-conditions"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles["terms-conditions-link-text"]}
                            >
                              Terms and Conditions
                            </Link>{" "}
                            and{" "}
                            <Link
                              href="https://www.herofincorp.com/data-privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles["terms-conditions-link-text"]}
                            >
                              Privacy Policy
                            </Link>
                            .
                          </span>
                        }
                        className={styles["white-text-color"]}
                      />
                      {/* <FormControlLabel
                        control={
                          <Checkbox
                            name="whatsappUpdates"
                            className={styles["login-select-btn"]}
                            checked={checkboxState.whatsappUpdates}
                            onChange={handleCheckboxChange}
                          />}
                        label={
                          <Box className={styles["whatsapp-icon-box"]}>
                            Stay updated on all the alerts on&nbsp;
                            <Image src="/whatsapp_icon.svg" alt="whatsapp icon" width={20} height={20} priority />
                            &nbsp;WhatsApp
                          </Box>
                        }
                        className={styles["white-text-color"]}
                      /> */}
                    </FormControl>

                    <Box sx={{ px: 4 }}>
                      {selectedLoginOption === "appId" ? (() => {
                        const isDisabled = !appIdNew || !checkboxState.agreeToTerms;

                        return (
                          <SubmitButtonComponent
                            variant="contained"
                            type="submit"
                            className={`${styles["login-submit-btn"]} ${isDisabled ? styles["disabled"] : styles["enabled"]}`}
                            disabled={isDisabled}
                            onClick={() => handleVerifyAppId(appIdNew)}
                          >
                            Get OTP
                          </SubmitButtonComponent>
                        );
                      })() : (() => {
                        const isDisabled = !(formik.isValid && formik.dirty && checkboxState.agreeToTerms);

                        return (
                          <SubmitButtonComponent
                            variant="contained"
                            type="submit"
                            className={`${styles["login-submit-btn"]} ${isDisabled ? styles["disabled"] : styles["enabled"]}`}
                            disabled={isDisabled}
                            onClick={() => handleVerifyPhoneNumber(phoneNumber)}
                          >
                            Get OTP
                          </SubmitButtonComponent>
                        );
                      })()}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
        <NumberFoundModal
          open={openNumberFoundModal}
          handleModalClose={handleNumberFoundModalClose}
          hitSendOtp={handleHitSendOtp}
          phoneNumber={phoneNumber}
        />

        <OtpModal
          open={openOtpModal}
          handleModalClose={handleOtpModalClose}
          verifyOtp={handleVerifyOtp}
          hitResendOtp={handleHitSendOtp}
          resendOtp={resendOtp}
          setResendOtp={setResendOtp}
          otp={otp}
          setOtp={setOtp}
        />
      </Paper>
    </>
  );
};

export default LoginScreen;
